// client/src/v2/components/sidebar/SchoolListSection.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import SchoolRow from "./SchoolRow";
import TransportPanel from "../transport/TransportPanel";
import InfoTip from "../InfoTip";

function formatTier(key) {
  if (!key) return "";
  const k = String(key);
  if (/^pa\d+$/i.test(k)) return k.toUpperCase();
  if (/^historic_nod_(\d{4})$/i.test(k)) {
    return `NOD ${k.slice(-4)}`;
  }
  return k.charAt(0).toUpperCase() + k.slice(1);
}

function makeHoverTitle(check) {
  const distance =
    typeof check.distanceKm === "number" ? ` (${check.distanceKm} km from school)` : "";

  if (!check?.inCatchment) {
    if (typeof check?.distanceKm === "number") {
      return `Beyond recent offer distances${distance}`;
    }
    return "";
  }

  const tier = formatTier(check.matchedBy);
  if (tier) return `Within recent offer distance: ${tier}${distance}`;
  return `In catchment${distance}`;
}

export default function SchoolListSection({
  title,
  titleTip,
  items,
  selectedIds,
  onToggle,
  /** Clears every catchment selection (all section headers). */
  onClearAllCatchments,

  // transport props
  homeLocation,
  transportSchool,
  onOpenTransport,
  onCloseTransport,
  onSelectRoute,
  onHoverRoute,
  onLeaveRoute,
  onLeaveOptionsList,
  onClearRoute,

  // catchment hover
  showCatchmentHover,
  catchmentCheckBySchoolId,
  catchmentLoading,

  // ✅ load-more + scroll container
  initialVisible,
  loadStep,
  showLoadMore,
  scrollHeight,
  /** Cap list length (e.g. nearest 20). */
  maxItems,
  emptyText,

  // ✅ additive
  onOpenDetails,
  onRowClick,
  drawerSchoolId,
  focusedSchoolId,

  /** Left panel: visual group — green | blue | yellow */
  sectionAccent,

  showViewAllButton = true,

  /** Tighter header for mobile bottom cards */
  compactMobileCard = false,

  /** Ref to the scrollable list container (mobile deck sync) */
  listRef = undefined,

  /**
   * Rebuilds the load-more window (home, search, favourites).
   * Selection and map focus must not be part of this key.
   */
  resetKey,

  isFavourite,
  onToggleFavourite,

  /** Append open-seat count in brackets on each row (Open To All section). */
  showOpenSeatsBracket = false,
}) {
  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const safeSelectedIds = useMemo(
    () => (Array.isArray(selectedIds) ? selectedIds : []),
    [selectedIds]
  );

  const initial = useMemo(() => {
    if (typeof initialVisible === "number" && initialVisible > 0)
      return initialVisible;
    return safeItems.length;
  }, [initialVisible, safeItems.length]);

  const step = typeof loadStep === "number" && loadStep > 0 ? loadStep : 10;
  const hardMax =
    typeof maxItems === "number" && maxItems > 0
      ? maxItems
      : safeItems.length;

  const pinIds = useMemo(() => {
    return new Set(
      [...safeSelectedIds, focusedSchoolId, drawerSchoolId].filter(
        (id) => id != null
      )
    );
  }, [safeSelectedIds, focusedSchoolId, drawerSchoolId]);

  // Soft cap at maxItems, but never drop map-selected / focused / drawer school
  // (bucket may have appended them past nearest-20). Keep home-distance order
  // from the parent bucket (safeItems is already nearest-first).
  const cappedItems = useMemo(() => {
    const base = safeItems.slice(0, hardMax);
    if (!pinIds.size) return base;
    const baseIds = new Set(base.map((s) => s.id));
    const extras = safeItems.filter(
      (s) => pinIds.has(s.id) && !baseIds.has(s.id)
    );
    if (!extras.length) return base;
    const keep = new Set([...baseIds, ...extras.map((s) => s.id)]);
    // Filter in safeItems order = distance order (do not append extras at end)
    return safeItems.filter((s) => keep.has(s.id));
  }, [safeItems, hardMax, pinIds]);

  const [visibleCount, setVisibleCount] = useState(initial);
  const localListRef = useRef(null);
  const resetKeyRef = useRef(resetKey);
  const scrolledFocusRef = useRef(null);

  // Keep parent listRef in sync without an unstable callback ref
  useEffect(() => {
    const el = localListRef.current;
    if (typeof listRef === "function") listRef(el);
    else if (listRef && typeof listRef === "object") listRef.current = el;
  });

  // Home, search, or favourites: start the window again. Selection does not.
  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    setVisibleCount(initial);
  }, [resetKey, initial]);

  // Keep a Load more expansion. Grow only so a selected or focused row
  // that is still hidden becomes visible. Never shrink on select.
  useEffect(() => {
    if (!showLoadMore) return;
    setVisibleCount((current) => {
      let next = Math.max(current, initial);
      if (pinIds.size && cappedItems.length) {
        cappedItems.forEach((s, i) => {
          if (pinIds.has(s.id)) next = Math.max(next, i + 1);
        });
      }
      if (cappedItems.length > 0) next = Math.min(next, cappedItems.length);
      return next === current ? current : next;
    });
  }, [initial, cappedItems, showLoadMore, pinIds]);

  const visibleItems = useMemo(() => {
    if (!showLoadMore) return cappedItems;
    return cappedItems.slice(0, visibleCount);
  }, [cappedItems, showLoadMore, visibleCount]);

  // Map pin: bring the focused row into view, including one that was already
  // loaded but sitting below the fold. A row already on screen stays put.
  useEffect(() => {
    if (focusedSchoolId == null) {
      scrolledFocusRef.current = null;
      return;
    }
    if (!visibleItems.some((s) => s.id === focusedSchoolId)) return;
    if (scrolledFocusRef.current === focusedSchoolId) return;
    const listEl = localListRef.current;
    if (!listEl) return;
    const idAttr =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(String(focusedSchoolId))
        : String(focusedSchoolId);

    const scrollRowTowardMiddle = (host, row) => {
      if (!host || !row) return false;
      const hostRect = host.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const pad = 8;
      const visible =
        rowRect.top >= hostRect.top + pad &&
        rowRect.bottom <= hostRect.bottom - pad;
      if (visible) return false;

      const rowCenter = rowRect.top + rowRect.height / 2;
      const hostCenter = hostRect.top + hostRect.height / 2;
      const delta = rowCenter - hostCenter;
      if (Math.abs(delta) <= 2) return false;
      host.scrollTo({
        top: Math.max(0, host.scrollTop + delta),
        behavior: "smooth",
      });
      return true;
    };

    let cancelled = false;
    let t2 = 0;
    let t3 = 0;
    const align = () => {
      if (cancelled) return;
      const row = listEl.querySelector(`[data-school-row-id="${idAttr}"]`);
      if (!row) return;
      scrolledFocusRef.current = focusedSchoolId;

      scrollRowTowardMiddle(listEl, row);

      const panelHost =
        listEl.closest(".v2-left-panel-scroll") ||
        listEl.closest(".v2-sidebar") ||
        listEl.closest(".v2-left-panel");
      if (panelHost && panelHost !== listEl) {
        scrollRowTowardMiddle(panelHost, row);
      }
    };

    const t1 = requestAnimationFrame(() => {
      t2 = requestAnimationFrame(align);
      t3 = window.setTimeout(align, 60);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(t1);
      cancelAnimationFrame(t2);
      window.clearTimeout(t3);
    };
  }, [focusedSchoolId, visibleItems]);

  const canLoadMore =
    !!showLoadMore &&
    visibleCount < cappedItems.length &&
    cappedItems.length > 0;

  const sectionClass = [
    "v2-section",
    sectionAccent ? `v2-section--accent-${sectionAccent}` : "",
    compactMobileCard ? "v2-section--mobile-card" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={sectionClass}>
      <div className="v2-section-header">
        <div className="v2-section-title">
          <span className="v2-info-tip--label">
            {title}
            {titleTip ? <InfoTip text={titleTip} label="About this list" /> : null}
          </span>
        </div>
        {showViewAllButton ? (
          <button
            type="button"
            className="v2-link-btn"
            disabled={!safeSelectedIds.length}
            onClick={() => onClearAllCatchments?.()}
          >
            Clear all
          </button>
        ) : null}
      </div>

      <div
        ref={localListRef}
        className="v2-section-list"
        style={
          typeof scrollHeight === "number" && scrollHeight > 0
            ? { maxHeight: scrollHeight, overflowY: "auto" }
            : undefined
        }
      >
        {visibleItems.length === 0 ? (
          <div style={{ fontSize: 13, color: "#666", padding: "6px 2px" }}>
            {catchmentLoading ? "Loading…" : (emptyText || "No schools to show.")}
          </div>
        ) : (
          visibleItems.map((s) => {
            const isOpen = !!transportSchool && transportSchool.id === s.id;

            const check = showCatchmentHover
              ? catchmentCheckBySchoolId?.[s.id]
              : undefined;

            let hoverTitle = "";
            if (showCatchmentHover) {
              if (check) hoverTitle = makeHoverTitle(check);
              else if (catchmentLoading) hoverTitle = "Checking catchment…";
            }

            return (
              <React.Fragment key={s.id}>
                <div data-school-row-id={s.id}>
                  <SchoolRow
                    school={s}
                    checked={safeSelectedIds.includes(s.id)}
                    onToggle={onToggle}
                    onOpenTransport={onOpenTransport}
                    hoverTitle={hoverTitle}
                    onOpenDetails={onOpenDetails}
                    onRowClick={onRowClick}
                    isDrawerOpen={drawerSchoolId != null && drawerSchoolId === s.id}
                    isTransportOpen={!!transportSchool && transportSchool.id === s.id}
                    isCatchmentSelected={safeSelectedIds.includes(s.id)}
                    isMapFocused={
                      focusedSchoolId != null && focusedSchoolId === s.id
                    }
                    isFavourite={!!isFavourite?.(s.id)}
                    onToggleFavourite={onToggleFavourite}
                    showOpenSeatsBracket={showOpenSeatsBracket}
                  />
                </div>

                {isOpen ? (
                  <div className="v2-transport-inline">
                    <TransportPanel
                      school={transportSchool}
                      homeLocation={homeLocation}
                      onClose={onCloseTransport}
                      onSelectRoute={onSelectRoute}
                      onHoverRoute={onHoverRoute}
                      onLeaveRoute={onLeaveRoute}
                      onLeaveOptionsList={onLeaveOptionsList}
                      onClearRoute={onClearRoute}
                      compactMobile={!!compactMobileCard}
                    />
                  </div>
                ) : null}
              </React.Fragment>
            );
          })
        )}
      </div>

      {canLoadMore ? (
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            className="v2-link-btn"
            onClick={() =>
              setVisibleCount((n) => Math.min(n + step, cappedItems.length))
            }
          >
            Load more
          </button>
        </div>
      ) : null}
    </div>
  );
}